// screens/staff/MetricheScreen.tsx
//
// W6 v1 Session 3 — read-only metrics dashboard per spec §7.2.12 + §8.4 #13 +
// §8.1.5. Three stat cards:
//
//   1. Tasso di rifiuto Chiedi (ultimi N gg) — vs <15% target
//   2. Costo medio per studente (USD, ultimi N gg) — per A/B variant breakdown
//   3. Tempo istruttore per studente (min, ultimi N gg) — heuristic from audit_logs
//
// Period toggle: 7gg / 30gg / 90gg.
//
// Visible only to titolare + instructor_teoria + instructor_guida (segreteria
// EXCLUDED). Italian "Lei" voice.

import { useEffect, useState, type JSX } from 'react';
import { useTokens } from '../../state/theme';
import {
  staffApi,
  type RefusalRatePayload,
  type CostPerStudentPayload,
  type InstructorTimePayload,
} from '../../api/staff';
import PeriodToggle, { type Period } from './PeriodToggle';

export default function MetricheScreen(): JSX.Element {
  const tokens = useTokens();
  const [days, setDays] = useState<Period>(30);
  const [refusal, setRefusal] = useState<RefusalRatePayload | null>(null);
  const [cost, setCost] = useState<CostPerStudentPayload | null>(null);
  const [time, setTime] = useState<InstructorTimePayload | null>(null);
  const [refusalErr, setRefusalErr] = useState<string | null>(null);
  const [costErr, setCostErr] = useState<string | null>(null);
  const [timeErr, setTimeErr] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setRefusalErr(null);
    setCostErr(null);
    setTimeErr(null);
    setRefusal(null);
    setCost(null);
    setTime(null);

    Promise.allSettled([
      staffApi.metricsRefusalRate(days),
      staffApi.metricsCostPerStudent(days),
      staffApi.metricsInstructorTime(days),
    ]).then(([refusalR, costR, timeR]) => {
      if (!alive) return;
      if (refusalR.status === 'fulfilled') setRefusal(refusalR.value);
      else setRefusalErr('Caricamento metrica non riuscito.');
      if (costR.status === 'fulfilled') setCost(costR.value);
      else setCostErr('Caricamento metrica non riuscito.');
      if (timeR.status === 'fulfilled') setTime(timeR.value);
      else setTimeErr('Caricamento metrica non riuscito.');
      setLoading(false);
    });

    return () => { alive = false; };
  }, [days]);

  return (
    <section data-testid="metriche-screen">
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: tokens.fonts.serif, fontSize: 26, fontWeight: 700, margin: 0 }}>
          Metriche del corso
        </h1>
        <p style={{ color: tokens.textMuted, margin: '4px 0 0 0', fontSize: 13 }}>
          Tre indicatori di salute del tutor. Aggiornati in tempo reale dalle ultime sessioni.
        </p>
      </header>

      {/* Period toggle (extracted to <PeriodToggle /> in W6 v1.1 for reuse on ClassPulseScreen) */}
      <div style={{ marginBottom: 24 }}>
        <PeriodToggle value={days} onChange={setDays} testidPrefix="metriche-period" />
      </div>

      <div
        data-testid="metriche-grid"
        style={{
          display:             'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap:                 16,
        }}
      >
        {/* Refusal rate card */}
        <StatCard
          testid="metric-refusal-rate"
          header={`Tasso di rifiuto Chiedi (ultimi ${days}gg)`}
          loading={loading}
          error={refusalErr}
        >
          {refusal && <RefusalRateBody refusal={refusal} />}
        </StatCard>

        {/* Cost card */}
        <StatCard
          testid="metric-cost"
          header={`Costo medio per studente (USD, ultimi ${days}gg)`}
          loading={loading}
          error={costErr}
        >
          {cost && <CostBody cost={cost} />}
        </StatCard>

        {/* Time card */}
        <StatCard
          testid="metric-time"
          header={`Tempo istruttore per studente (min, ultimi ${days}gg)`}
          loading={loading}
          error={timeErr}
        >
          {time && <TimeBody time={time} />}
        </StatCard>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Stat-card primitives + bodies
// ---------------------------------------------------------------------------

function StatCard({
  testid,
  header,
  loading,
  error,
  children,
}: {
  testid:   string;
  header:   string;
  loading:  boolean;
  error:    string | null;
  children: React.ReactNode;
}): JSX.Element {
  const tokens = useTokens();
  return (
    <div
      data-testid={testid}
      style={{
        background:    tokens.surface,
        border:        `1px solid ${tokens.line}`,
        borderRadius:  tokens.radius.lg,
        padding:       '20px 22px',
        minHeight:     180,
      }}
    >
      <h2 style={{
        fontSize:      11,
        fontWeight:    700,
        letterSpacing: 1.0,
        color:         tokens.textMuted,
        margin:        '0 0 12px 0',
        textTransform: 'uppercase',
      }}>
        {header}
      </h2>
      {loading ? (
        <div style={{ color: tokens.textMuted, fontSize: 13 }}>Caricamento…</div>
      ) : error ? (
        <div role="alert" style={{ color: tokens.wrong, fontSize: 13 }}>{error}</div>
      ) : children}
    </div>
  );
}

function RefusalRateBody({ refusal }: { refusal: RefusalRatePayload }): JSX.Element {
  const tokens = useTokens();
  // Color coding: green ≤target, yellow ≤target+5pts, red above.
  // Plus a non-color cue (icon + label) for WCAG — color alone is not a
  // sufficient signal for users with color-vision deficiency.
  const target = refusal.target_rate;
  let color: string = tokens.right;
  let cueIcon = '✓';
  let cueLabel = 'Sotto obiettivo';
  if (refusal.rate > target + 0.05) {
    color = tokens.wrong;
    cueIcon = '✕';
    cueLabel = 'Sopra obiettivo';
  } else if (refusal.rate > target) {
    color = tokens.accent;
    cueIcon = '!';
    cueLabel = 'Vicino all\'obiettivo';
  }

  if (refusal.total_count === 0) {
    return (
      <div data-testid="metric-refusal-rate-empty" style={{ fontSize: 13, color: tokens.textMuted }}>
        Nessuna domanda Chiedi negli ultimi {refusal.period_days} giorni.
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span data-testid="metric-refusal-rate-value" style={{ fontFamily: tokens.fonts.serif, fontSize: 36, fontWeight: 700, lineHeight: 1, color }}>
          {(refusal.rate * 100).toFixed(1)}%
        </span>
        <span style={{ color: tokens.textMuted, fontSize: 13 }}>obiettivo &lt; {(target * 100).toFixed(0)}%</span>
      </div>
      <div data-testid="metric-refusal-rate-cue" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span aria-hidden="true" style={{ fontSize: 12, color, fontWeight: 700 }}>{cueIcon}</span>
        <span style={{ fontSize: 12, color: tokens.textMuted, fontWeight: 600 }}>{cueLabel}</span>
      </div>
      <div style={{ fontSize: 13, color: tokens.textMuted }}>
        {refusal.refused_count} rifiuti su {refusal.total_count} domande
      </div>
      {/* FP9 — real spend + volume off the same prof_turn_telemetry rows. */}
      {refusal.avg_cost_usd != null && (
        <div data-testid="metric-refusal-rate-cost" style={{ fontSize: 12, color: tokens.textMuted, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${tokens.line}` }}>
          Costo medio {fmtUsd(refusal.avg_cost_usd)}/turno · {refusal.turn_count ?? refusal.total_count} turni
        </div>
      )}
    </div>
  );
}

function CostBody({ cost }: { cost: CostPerStudentPayload }): JSX.Element {
  const tokens = useTokens();
  if (cost.session_count === 0) {
    return (
      <div data-testid="metric-cost-empty" style={{ fontSize: 13, color: tokens.textMuted }}>
        Nessuna lezione lunga (Deep) negli ultimi {cost.period_days} giorni.
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span data-testid="metric-cost-value" style={{ fontFamily: tokens.fonts.serif, fontSize: 36, fontWeight: 700, lineHeight: 1 }}>
          {fmtUsd(cost.avg_cost_usd)}
        </span>
        <span style={{ color: tokens.textMuted, fontSize: 13 }}>/ studente</span>
      </div>
      <div style={{ fontSize: 13, color: tokens.textMuted, marginBottom: 8 }}>
        {cost.session_count} lezion{cost.session_count === 1 ? 'e' : 'i'} su {cost.student_count} student{cost.student_count === 1 ? 'e' : 'i'} · totale {fmtUsd(cost.total_cost_usd)}
      </div>
      {cost.breakdown_by_variant.length > 1 && (
        <div data-testid="metric-cost-variants" style={{ fontSize: 12, color: tokens.textMuted, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${tokens.line}` }}>
          {cost.breakdown_by_variant.map(v => {
            // Non-color WCAG cue per variant — pairs with passes_target so
            // a user with color-vision deficiency still gets a clear signal.
            const badgeColor = v.passes_target ? tokens.right : tokens.wrong;
            const badgeIcon  = v.passes_target ? '✓' : '✕';
            const badgeLabel = v.passes_target ? 'Entro obiettivo' : 'Sopra obiettivo';
            return (
              <div key={v.variant} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                <span>{v.variant}: {fmtUsd(v.mean_cost_usd)} medio ({v.count} lezioni)</span>
                <span data-testid={`metric-cost-variant-cue-${v.variant}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span aria-hidden="true" style={{ color: badgeColor, fontWeight: 700 }}>{badgeIcon}</span>
                  <span style={{ color: badgeColor, fontWeight: 600 }}>{badgeLabel}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TimeBody({ time }: { time: InstructorTimePayload }): JSX.Element {
  const tokens = useTokens();
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span data-testid="metric-time-value" style={{ fontFamily: tokens.fonts.serif, fontSize: 36, fontWeight: 700, lineHeight: 1 }}>
          {time.avg_minutes_per_student.toFixed(1)}
        </span>
        <span style={{ color: tokens.textMuted, fontSize: 13 }}>min / studente</span>
      </div>
      <div style={{ fontSize: 13, color: tokens.textMuted, marginBottom: 4 }}>
        {time.read_event_count} consultazioni su {time.student_count} student{time.student_count === 1 ? 'e' : 'i'}
      </div>
      {time.read_event_count === 0 && (
        <div data-testid="metric-time-empty-disclaimer" style={{ fontSize: 12, color: tokens.textMuted, fontStyle: 'italic', marginTop: 8 }}>
          Il conteggio inizia con la presente sessione: il dato cresce man mano che gli istruttori consultano le pagine studente.
        </div>
      )}
    </div>
  );
}

function fmtUsd(n: number): string {
  // Two decimals, US-style $ prefix (cost telemetry is in USD per Anthropic billing).
  return `$${n.toFixed(2)}`;
}
